import { useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  enabled: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
}

/**
 * Observes a sentinel element and calls `onLoadMore` when it scrolls into view.
 * The observer only runs while `enabled` is true, so callers can pause it while a
 * request is in flight (e.g. `enabled = canLoadMore && !loading`).
 */
const useInfiniteScroll = <T extends HTMLElement = HTMLDivElement>(options: UseInfiniteScrollOptions) => {
  const { enabled, onLoadMore, rootMargin = '200px' } = options;
  const sentinelRef = useRef<T | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !enabled) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { root: null, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  return sentinelRef;
};

export default useInfiniteScroll;
